import * as React from 'react';

import { cn } from '../../../src/lib/utils';
import { buttonVariants, Button as AppWebButton } from '../../../src/components/ui/button';
import { AppText } from '../../core';
import { Checkbox as AppCheckboxBase } from '../../../src/components/ui/checkbox';
import {
  Select as AppSelectBase,
  SelectContent as AppSelectContentBase,
  SelectItem as AppSelectItemBase,
  SelectTrigger as AppSelectTriggerBase,
  SelectValue as AppSelectValueBase,
} from './web-collections';
import {
  Dialog as AppDialogBase,
  DialogContent as AppDialogContentBase,
  DialogDescription as AppDialogDescriptionBase,
  DialogFooter as AppDialogFooterBase,
  DialogHeader as AppDialogHeaderBase,
  DialogTitle as AppDialogTitleBase,
  DialogTrigger as AppDialogTriggerBase,
  Popover as AppPopoverBase,
  PopoverContent as AppPopoverContentBase,
  PopoverTrigger as AppPopoverTriggerBase,
  Sheet as AppSheetBase,
  SheetContent as AppSheetContentBase,
  SheetDescription as AppSheetDescriptionBase,
  SheetHeader as AppSheetHeaderBase,
  SheetTitle as AppSheetTitleBase,
  SheetTrigger as AppSheetTriggerBase,
  TooltipProvider as AppTooltipProviderBase,
  type WebToastActionElement as AppToastActionElement,
  type WebToastProps as AppToastProps,
} from './web-overlays';
export { AppWebFileInput } from './fileInput';

// Keep web-only UI adapters behind ui-foundation so app code does not bypass the shared UI boundary.
export { Badge as AppBadge } from '../../../src/components/ui/badge';
export {
  Avatar as AppAvatar,
  AvatarFallback as AppAvatarFallback,
  AvatarImage as AppAvatarImage,
} from '../../../src/components/ui/avatar';
export {
  Accordion as AppAccordion,
  AccordionContent as AppAccordionContent,
  AccordionItem as AppAccordionItem,
  AccordionTrigger as AppAccordionTrigger,
} from '../../../src/components/ui/accordion';
export { AppCalendar } from './web-collections';
export { AppCheckboxBase as AppCheckbox };
export {
  AppDialogBase as AppDialog,
  AppDialogContentBase as AppDialogContent,
  AppDialogDescriptionBase as AppDialogDescription,
  AppDialogFooterBase as AppDialogFooter,
  AppDialogHeaderBase as AppDialogHeader,
  AppDialogTitleBase as AppDialogTitle,
  AppDialogTriggerBase as AppDialogTrigger,
};
export { Input as AppWebInput } from '../../../src/components/ui/input';
export { Textarea as AppWebTextarea } from '../../../src/components/ui/textarea';
export { Label as AppLabel } from '../../../src/components/ui/label';
export {
  AppPopoverBase as AppPopover,
  AppPopoverContentBase as AppPopoverContent,
  AppPopoverTriggerBase as AppPopoverTrigger,
};
export { Progress as AppProgress } from '../../../src/components/ui/progress';
export {
  AppSelectBase as AppSelect,
  AppSelectContentBase as AppSelectContent,
  AppSelectItemBase as AppSelectItem,
  AppSelectTriggerBase as AppSelectTrigger,
  AppSelectValueBase as AppSelectValue,
};
export {
  AppSheetBase as AppSheet,
  AppSheetContentBase as AppSheetContent,
  AppSheetDescriptionBase as AppSheetDescription,
  AppSheetHeaderBase as AppSheetHeader,
  AppSheetTitleBase as AppSheetTitle,
  AppSheetTriggerBase as AppSheetTrigger,
};
export { Toaster as AppSonner } from '../../../src/components/ui/sonner';
export { Switch as AppSwitch } from '../../../src/components/ui/switch';
export {
  Tabs as AppTabs,
  TabsContent as AppTabsContent,
  TabsList as AppTabsList,
  TabsTrigger as AppTabsTrigger,
} from './web-collections';
export { Toaster as AppToaster } from '../../../src/components/ui/toaster';
export { AppTooltipProviderBase as AppTooltipProvider };
export { AppWebButton };
export type { AppToastActionElement, AppToastProps };

type AppIconButtonProps = React.ComponentProps<typeof AppWebButton>;

export function AppIconButton({
  type = 'button',
  variant = 'ghost',
  size = 'icon',
  className,
  ...props
}: AppIconButtonProps) {
  return (
    <AppWebButton
      type={type}
      variant={variant}
      size={size}
      className={cn('h-8 w-8 rounded-lg', className)}
      {...props}
    />
  );
}

type AppChipButtonProps = React.ComponentProps<typeof AppWebButton> & {
  active?: boolean;
};

export function AppChipButton({
  active = false,
  type = 'button',
  variant,
  size = 'sm',
  className,
  ...props
}: AppChipButtonProps) {
  return (
    <AppWebButton
      type={type}
      variant={variant ?? 'outline'}
      size={size}
      className={cn(
        'h-auto rounded-full px-3 py-2 text-xs font-medium transition-all active:scale-[0.98]',
        active
          ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary'
          : 'border-border bg-background text-foreground hover:bg-muted/60 hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

type AppPanelButtonProps = React.ComponentProps<typeof AppWebButton>;

export function AppPanelButton({
  type = 'button',
  variant = 'outline',
  className,
  ...props
}: AppPanelButtonProps) {
  return (
    <AppWebButton
      type={type}
      variant={variant}
      className={cn(
        'h-auto w-full justify-start whitespace-normal rounded-xl px-4 py-2.5 text-left transition-all active:scale-[0.98]',
        className,
      )}
      {...props}
    />
  );
}

type AppTextLinkButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export const appTextLinkButtonClassName =
  cn(buttonVariants({ variant: 'link' }), 'h-auto p-0 font-medium underline-offset-2 active:scale-95');

export function AppTextLinkButton({
  type = 'button',
  className,
  ...props
}: AppTextLinkButtonProps) {
  return <AppWebButton type={type} variant="link" className={cn('h-auto p-0 font-medium underline-offset-2 active:scale-95', className)} {...props} />;
}

type AppNativeSelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  options?: ParsedSelectOption[];
  placeholder?: string;
};

type ParsedSelectOption = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
};

const EMPTY_SELECT_VALUE = '__adulting_empty_select__';

function renderSelectLabel(label: React.ReactNode, color: '$color' | '$colorMuted') {
  if (typeof label === 'string' || typeof label === 'number') {
    return (
      <AppText fontSize="$sm" color={color}>
        {label}
      </AppText>
    );
  }

  return label;
}

function parseSelectOptions(children: React.ReactNode): ParsedSelectOption[] {
  const options: ParsedSelectOption[] = [];

  const collect = (node: React.ReactNode) => {
    React.Children.forEach(node, (child) => {
      if (!React.isValidElement(child)) {
        return;
      }

      if (child.type === React.Fragment || child.type === 'optgroup') {
        collect(child.props.children);
        return;
      }

      if (child.type !== 'option') {
        return;
      }

      const optionValue = child.props.value == null ? String(child.props.children ?? '') : String(child.props.value);
      options.push({
        value: optionValue,
        label: child.props.children,
        disabled: Boolean(child.props.disabled),
      });
    });
  };

  collect(children);
  return options;
}

export function AppNativeSelect({
  className,
  children,
  options: providedOptions,
  value,
  defaultValue,
  onChange,
  disabled,
  placeholder,
  ...props
}: AppNativeSelectProps) {
  const options = React.useMemo(
    () => providedOptions ?? parseSelectOptions(children),
    [children, providedOptions],
  );
  const initialValue = defaultValue == null
    ? options[0]?.value ?? ''
    : String(defaultValue);
  const [uncontrolledValue, setUncontrolledValue] = React.useState(initialValue);
  const isControlled = value != null;
  const selectedValue = isControlled ? String(value) : uncontrolledValue;
  const internalValue = selectedValue === '' ? EMPTY_SELECT_VALUE : selectedValue;
  const selectedOption = options.find((option) => option.value === selectedValue) ?? null;
  const fallbackOption = options.find((option) => option.value === '') ?? null;
  const selectedLabel = selectedOption?.label ?? fallbackOption?.label ?? placeholder ?? '';

  const handleValueChange = (nextInternalValue: string) => {
    const nextValue = nextInternalValue === EMPTY_SELECT_VALUE ? '' : nextInternalValue;
    if (!isControlled) {
      setUncontrolledValue(nextValue);
    }
    onChange?.({
      target: { value: nextValue } as HTMLSelectElement,
      currentTarget: { value: nextValue } as HTMLSelectElement,
    } as React.ChangeEvent<HTMLSelectElement>);
  };

  return (
    <AppSelectBase
      value={internalValue}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <AppSelectTriggerBase
        id={props.id}
        aria-label={props['aria-label']}
        className={cn('w-full rounded-lg', className)}
      >
        {renderSelectLabel(selectedLabel, selectedValue === '' ? '$colorMuted' : '$color')}
      </AppSelectTriggerBase>
      <AppSelectContentBase>
        {options.map((option) => (
          <AppSelectItemBase
            key={`${option.value || 'empty'}-${String(option.label)}`}
            value={option.value === '' ? EMPTY_SELECT_VALUE : option.value}
            disabled={option.disabled}
          >
            {option.label}
          </AppSelectItemBase>
        ))}
      </AppSelectContentBase>
    </AppSelectBase>
  );
}

type AppNativeCheckboxProps = React.InputHTMLAttributes<HTMLInputElement>;

export function AppNativeCheckbox({
  className,
  checked,
  defaultChecked,
  onChange,
  disabled,
  ...props
}: AppNativeCheckboxProps) {
  const [uncontrolledChecked, setUncontrolledChecked] = React.useState(Boolean(defaultChecked));
  const isControlled = checked != null;
  const resolvedChecked = isControlled ? Boolean(checked) : uncontrolledChecked;

  const handleCheckedChange = (nextState: boolean | 'indeterminate') => {
    const nextChecked = nextState === true;
    if (!isControlled) {
      setUncontrolledChecked(nextChecked);
    }
    onChange?.({
      target: { checked: nextChecked } as HTMLInputElement,
      currentTarget: { checked: nextChecked } as HTMLInputElement,
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <AppCheckboxBase
      checked={resolvedChecked}
      onCheckedChange={handleCheckedChange}
      disabled={disabled}
      className={cn('rounded border-border', className)}
      aria-label={props['aria-label']}
    />
  );
}
